/**
 * EvaluationEditSheet — right-edge slide-over for editing an evaluation's
 * core details (name, market area, description) and its Active state, plus a
 * read-only brief summary built from the Sites → Approvals tabs.
 *
 * Invoked by clicking the evaluation name in the detail header. Mirrors the
 * Projects/Portfolios edit-drawer UX, built on the shared `Sheet` primitive.
 *
 * The summary is generated server-side (GET /api/evaluations/:id/summary) from
 * live tab data, so it always reflects the current evaluation rather than a
 * stored, drifting field.
 *
 * Also hosts Delete, in the footer, mirroring LocationActionSheet's two-step
 * inline confirm. Deletion is only offered while the evaluation has no sites —
 * see the note on `siteCount` below.
 */

import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Sheet } from 'components/ui/sheet'
import { Input } from 'components/ui/input'
import { Textarea } from 'components/ui/textarea'
import { Label } from 'components/ui/label'
import { Button } from 'components/ui/button'
import { Switch } from 'components/ui/switch'
import { ClipboardCheck, AlertCircle, Loader2, Trash2 } from 'lucide-react'
import {
	updateEvaluation,
	fetchEvaluationSummary,
	deleteEvaluation,
} from 'slices/evaluationSlice'
import PopupCloseButton from 'components/PopupCloseButton'

interface SummaryItem {
	label: string
	value: string
}

interface EvaluationEditSheetProps {
	open: boolean
	onClose: () => void
	evaluation: any
	/** Called after a successful delete, so the host can navigate away. */
	onDeleted?: () => void
}

const EvaluationEditSheet = ({
	open,
	onClose,
	evaluation,
	onDeleted,
}: EvaluationEditSheetProps) => {
	const dispatch = useDispatch()

	const [name, setName] = useState('')
	const [marketArea, setMarketArea] = useState('')
	const [description, setDescription] = useState('')
	const [isActive, setIsActive] = useState(true)
	const [submitting, setSubmitting] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [showDeleteConfirm, setShowDeleteConfirm] = useState(false)
	const [deleting, setDeleting] = useState(false)

	const [summaryItems, setSummaryItems] = useState<SummaryItem[]>([])
	const [summaryLoading, setSummaryLoading] = useState(false)

	// Re-seed local state whenever the drawer opens or targets a different
	// evaluation, so a reused instance never shows a stale entity's values.
	useEffect(() => {
		if (open && evaluation) {
			setName(evaluation.name || '')
			setMarketArea(evaluation.marketArea || '')
			setDescription(evaluation.description || '')
			setIsActive(evaluation.isActive !== false)
			setSubmitting(false)
			setError(null)
			setShowDeleteConfirm(false)
			setDeleting(false)
		}
	}, [open, evaluation])

	// Pull the live per-tab summary each time the drawer opens.
	useEffect(() => {
		if (!open || !evaluation?._id) return
		let cancelled = false
		setSummaryLoading(true)
		setSummaryItems([])
		;(async () => {
			// @ts-ignore — thunks
			const result: any = await dispatch(fetchEvaluationSummary(evaluation._id))
			if (cancelled) return
			setSummaryItems(result?.payload?.items || [])
			setSummaryLoading(false)
		})()
		return () => {
			cancelled = true
		}
	}, [dispatch, open, evaluation?._id])

	const handleSubmit = useCallback(async () => {
		if (!name.trim()) {
			setError('Name is required')
			return
		}
		if (!evaluation?._id) return
		setError(null)
		setSubmitting(true)
		try {
			// @ts-ignore — thunks
			const result: any = await dispatch(
				updateEvaluation({
					_id: evaluation._id,
					name: name.trim(),
					marketArea: marketArea.trim(),
					description: description.trim(),
					isActive,
				}),
			)
			if (result?.error) {
				setError(result.payload || 'Failed to save evaluation')
				return
			}
			onClose()
		} catch (err: any) {
			setError(err?.message || 'Failed to save evaluation')
		} finally {
			setSubmitting(false)
		}
	}, [dispatch, evaluation, name, marketArea, description, isActive, onClose])

	/**
	 * How many sites stand in the way of deleting this evaluation.
	 *
	 * `stats.siteCount` comes from the detail endpoint, `siteCount` from the
	 * list endpoint; whichever loaded most recently is right. When NEITHER has
	 * loaded the count is genuinely unknown, and we deliberately do not treat
	 * that as zero — the delete button stays available and the server's 409 is
	 * what actually decides. Guessing wrong in the other direction would hide
	 * the action from someone entitled to use it.
	 */
	const siteCount: number | undefined =
		evaluation?.stats?.siteCount ?? evaluation?.siteCount

	const blockedBySites = typeof siteCount === 'number' && siteCount > 0

	const handleDelete = useCallback(async () => {
		if (!evaluation?._id) return
		setError(null)
		setDeleting(true)
		try {
			// @ts-ignore — thunks
			const result: any = await dispatch(deleteEvaluation(evaluation._id))
			if (result?.meta?.requestStatus !== 'fulfilled') {
				// Carries the server's 409 text, which names the blocking count.
				setError(
					typeof result?.payload === 'string'
						? result.payload
						: 'Failed to delete evaluation',
				)
				setShowDeleteConfirm(false)
				return
			}
			onClose()
			// The evaluation no longer exists, so the host has to leave the
			// detail route rather than render a missing entity.
			onDeleted?.()
		} finally {
			setDeleting(false)
		}
	}, [dispatch, evaluation, onClose, onDeleted])

	return (
		<Sheet open={open} onClose={onClose} side="right" className="w-[500px] max-w-[90vw]">
			{/* Header */}
			<div className="flex items-center gap-3 px-5 py-4 border-b border-gray-200">
				<div className="w-10 h-10 rounded-lg flex items-center justify-center bg-[#0080fe]/10 text-[#0080fe] shrink-0">
					<ClipboardCheck className="w-5 h-5" />
				</div>
				<div className="min-w-0 flex-1">
					<h2 className="text-lg font-semibold text-gray-900 truncate">
						{evaluation?.name || 'Edit evaluation'}
					</h2>
					<p className="text-sm text-gray-500">Edit evaluation details and settings</p>
				</div>
				<PopupCloseButton onClose={onClose} />
			</div>

			{/* Body */}
			<div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
				{/* Brief summary — read-only, built from the Sites → Approvals tabs. */}
				<div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
					<div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
						Summary
					</div>
					{summaryLoading ? (
						<div className="flex items-center gap-2 text-sm text-gray-500 py-1">
							<Loader2 className="w-4 h-4 animate-spin" />
							Building summary…
						</div>
					) : summaryItems.length === 0 ? (
						<p className="text-sm text-gray-400">No summary available.</p>
					) : (
						<dl className="grid grid-cols-1 gap-1.5">
							{summaryItems.map((item) => (
								<div key={item.label} className="flex items-baseline justify-between gap-3">
									<dt className="text-xs text-gray-500 shrink-0">{item.label}</dt>
									<dd className="text-sm text-gray-800 text-right">{item.value}</dd>
								</div>
							))}
						</dl>
					)}
				</div>

				<div>
					<Label htmlFor="evalEditName" className="mb-1.5 block">
						Name *
					</Label>
					<Input
						id="evalEditName"
						value={name}
						onChange={(e) => setName(e.target.value)}
						placeholder="Q2 2026 Site Selection"
						autoFocus
					/>
				</div>

				<div>
					<Label htmlFor="evalEditMarket" className="mb-1.5 block">
						Market Area
					</Label>
					<Input
						id="evalEditMarket"
						value={marketArea}
						onChange={(e) => setMarketArea(e.target.value)}
						placeholder="e.g. Greater Austin, TX"
					/>
				</div>

				<div>
					<Label htmlFor="evalEditDesc" className="mb-1.5 block">
						Description
					</Label>
					<Textarea
						id="evalEditDesc"
						value={description}
						onChange={(e) => setDescription(e.target.value)}
						placeholder="Longer description / context"
						className="min-h-[90px]"
					/>
				</div>

				{/* Active / Inactive toggle — mirrors the Projects/Portfolios pattern. */}
				<div className="flex items-start gap-3 py-2">
					<Switch
						name="isActive"
						checked={isActive}
						onChange={(e) => setIsActive(e.target.checked)}
					/>
					<span className="text-sm text-gray-700 leading-snug">
						{isActive
							? 'Active: This evaluation is marked active.'
							: 'Inactive: This evaluation is marked inactive.'}
					</span>
				</div>

				{error && (
					<div className="flex items-start gap-2 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm">
						<AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
						<span>{error}</span>
					</div>
				)}
			</div>

			{/* Footer — Delete on the left, Save on the right. Two-step inline
			    confirm, matching LocationActionSheet. */}
			<div className="border-t border-gray-200 px-5 py-3 flex items-center justify-between gap-3">
				<div className="min-w-0">
					{blockedBySites ? (
						<span className="text-xs text-gray-500">
							Remove {siteCount === 1 ? 'its site' : `all ${siteCount} sites`} on the Sites
							tab to delete this evaluation.
						</span>
					) : showDeleteConfirm ? (
						<div className="flex items-center gap-2">
							<span className="text-sm text-red-600 whitespace-nowrap">Delete it?</span>
							<Button
								variant="danger"
								onClick={handleDelete}
								disabled={deleting}
								className="gap-1.5"
							>
								{deleting ? (
									<>
										<Loader2 className="w-4 h-4 animate-spin" />
										Deleting…
									</>
								) : (
									'Yes, Delete'
								)}
							</Button>
							<Button
								variant="ghost"
								onClick={() => setShowDeleteConfirm(false)}
								disabled={deleting}
							>
								Cancel
							</Button>
						</div>
					) : (
						<Button
							variant="ghost-red"
							onClick={() => setShowDeleteConfirm(true)}
							disabled={submitting}
						>
							<Trash2 className="w-4 h-4 mr-1" />
							Delete
						</Button>
					)}
				</div>

				<div className="flex shrink-0 items-center gap-2">
					<Button variant="ghost" onClick={onClose} disabled={submitting || deleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						onClick={handleSubmit}
						disabled={submitting || deleting}
						className="gap-1.5"
					>
						{submitting ? (
							<>
								<Loader2 className="w-4 h-4 animate-spin" />
								Saving…
							</>
						) : (
							'Save changes'
						)}
					</Button>
				</div>
			</div>
		</Sheet>
	)
}

export default EvaluationEditSheet
